import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { io } from "socket.io-client";
import Navbar from "../components/Navbar";
import BackBar from "../components/Ticketing/BackBar";
import { apiGetShowing, apiCreateHold } from "../api/ticketingApi";
import { setCurrentHold } from "../redux/ticketingSlice";
import { fmtDateTime, fmtMoney, experienceFormat } from "../components/Ticketing/format";
import { goToSignIn } from "../utils/signInReturn";

const MAX_UNITS = 10;
const contrastText = (hex) => {
  const rgb = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255);
  const lum = rgb.map((v) => v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4)
    .reduce((sum, v, i) => sum + v * [0.2126, 0.7152, 0.0722][i], 0);
  return (lum + 0.05) / 0.05 >= 1.05 / (lum + 0.05) ? "#0D1A2F" : "#FFFFFF";
};

const SeatSelectionPage = () => {
  const { showingId } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const currentUser = useSelector((s) => s.user?.currentUser);

  const [data, setData] = useState(null);
  const [seatStatus, setSeatStatus] = useState({});
  const [selected, setSelected] = useState([]); // reserved seating
  const [gaQty, setGaQty] = useState({}); // { ticketTypeKey: qty }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState("");
  const socketRef = useRef(null);

  const load = () => {
    apiGetShowing(showingId)
      .then((d) => {
        setData(d);
        setSeatStatus(d.seatStatus || {});
        setLoading(false);
      })
      .catch(() => {
        setError("We couldn't load this showtime.");
        setLoading(false);
      });
  };

  useEffect(() => {
    load();
    // Live availability: other buyers' holds/sales update the map in real time.
    const socket = io(process.env.REACT_APP_SOCKET_URL, {
      transports: ["websocket", "polling"],
    });
    socketRef.current = socket;
    // Re-join AND re-fetch. Socket.IO does not replay missed events, so a
    // reconnect after a tunnel or lift used to leave the map showing seats as
    // AVAILABLE that had sold while we were offline. The customer then picked
    // them and the reserve call failed on seats the UI insisted were free.
    let firstConnect = true;
    socket.on("connect", () => {
      socket.emit("join", `showing:${showingId}`);
      if (firstConnect) { firstConnect = false; return; }
      load();
    });
    const applyStatus = (seatIds, status) => {
      if (!Array.isArray(seatIds) || !seatIds.length) return;
      setSeatStatus((prev) => {
        const next = { ...prev };
        for (const id of seatIds) next[id] = status;
        return next;
      });
      setSelected((prev) => prev.filter((id) => !seatIds.includes(id)));
    };
    socket.on("seatUpdate", (p) => applyStatus(p?.seatIds, p?.status));
    socket.on("seatsSold", (p) => applyStatus(p?.seatIds, "SOLD"));
    return () => {
      socket.emit("leave", `showing:${showingId}`);
      socket.off("seatUpdate");
      socket.off("seatsSold");
      socket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showingId]);

  const showing = data?.showing;
  const event = data?.event;
  const venue = data?.venue;
  const organizer = data?.organizer;
  const themeColor = /^#[0-9A-F]{6}$/i.test(organizer?.theme?.primaryColor || "") ? organizer.theme.primaryColor : "#83b524";
  const onThemeColor = contrastText(themeColor);
  const isReserved = ["RESERVED_SEATING", "RESERVED_UNITS"].includes(showing?.inventoryModel);
  const reservedUnitLabel = showing?.inventoryUnitLabel || "Seat";

  // seatClass -> price, from the showing's ticket types.
  const classPrice = useMemo(() => {
    const m = {};
    for (const tt of showing?.ticketTypes || []) {
      for (const cls of tt.seatClasses || []) m[cls] = Number(tt.price || 0);
    }
    return m;
  }, [showing]);

  const seatPrice = (seat) => classPrice[seat.seatClass] ?? 0;

  const toggleSeat = (seat) => {
    const status = seatStatus[seat.seatId] || "AVAILABLE";
    if (status !== "AVAILABLE") return;
    setNotice("");
    setSelected((prev) => {
      if (prev.includes(seat.seatId)) return prev.filter((x) => x !== seat.seatId);
      if (prev.length >= MAX_UNITS) {
        setNotice(`You can select up to ${MAX_UNITS} ${reservedUnitLabel.toLowerCase()}s.`);
        return prev;
      }
      return [...prev, seat.seatId];
    });
  };

  const reservedTotal = useMemo(() => {
    if (!isReserved || !showing) return 0;
    const priceById = {};
    for (const sec of data?.seatMap?.sections || [])
      for (const row of sec.rows || [])
        for (const seat of row.seats || []) priceById[seat.seatId] = seatPrice(seat);
    return selected.reduce((a, id) => a + (priceById[id] || 0), 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, data, showing, isReserved]);

  const gaTotal = useMemo(() => {
    let t = 0;
    for (const tt of showing?.ticketTypes || []) t += Number(tt.price || 0) * (gaQty[tt.key] || 0);
    return t;
  }, [gaQty, showing]);

  const gaCount = Object.values(gaQty).reduce((a, b) => a + b, 0);
  const total = isReserved ? reservedTotal : gaTotal;
  const count = isReserved ? selected.length : gaCount;

  const reserve = async () => {
    if (!currentUser) {
      goToSignIn(history);
      return;
    }
    if (count === 0) return;
    setSubmitting(true);
    setNotice("");
    try {
      const body = isReserved
        ? { showingId, seatIds: selected }
        : {
            showingId,
            gaItems: Object.entries(gaQty)
              .filter(([, q]) => q > 0)
              .map(([ticketTypeKey, qty]) => ({ ticketTypeKey, qty })),
          };
      const { hold } = await apiCreateHold(body);
      dispatch(setCurrentHold(hold));
      history.push(`/ticket-checkout/${hold._id}`);
    } catch (err) {
      const msg = err?.response?.data?.message || "That inventory is no longer available. Please select again.";
      setNotice(msg);
      load(); // refresh availability after a conflict
      setSelected([]);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Navbar />
      <Page style={{ "--cinema-primary": themeColor, "--cinema-on-primary": onThemeColor }}>
        <BackBar fallback="/events" />
        {loading && <Muted>Loading availability…</Muted>}
        {!loading && error && <Muted>{error}</Muted>}
        {!loading && !error && showing && (
          <>
            <Head>
              <h1>{event?.title}</h1>
              <Meta>
                {fmtDateTime(showing.startsAt)}
                {venue?.name ? ` · ${venue.name}` : ""}
              </Meta>
              <ShowMeta>{[experienceFormat(showing.presentation?.format), showing.presentation?.dimension, showing.presentation?.sound, showing.presentation?.screenName].filter(Boolean).join(" · ")}</ShowMeta>
              {data.inventoryProvider?.authoritative && <LiveInventory>Live seat availability from the cinema</LiveInventory>}
            </Head>

            {isReserved ? (
              <>
                <Screen>{showing.inventoryModel === "RESERVED_SEATING" ? "SCREEN" : `SELECT ${reservedUnitLabel.toUpperCase()}`}</Screen>
                <MapWrap>
                  {(data?.seatMap?.sections || []).map((sec, si) => (
                    <Section key={si}>
                      {sec.name && sec.name !== "Main" && <SecName>{sec.name}</SecName>}
                      {(sec.rows || []).map((row, ri) => (
                        <Row key={ri}>
                          <RowLabel>{row.rowLabel}</RowLabel>
                          <Seats>
                            {(row.seats || [])
                              .slice()
                              .sort((a, b) => (a.colIndex || 0) - (b.colIndex || 0))
                              .map((seat) => {
                                const status = seat.blocked
                                  ? "BLOCKED"
                                  : seatStatus[seat.seatId] || "AVAILABLE";
                                const isSel = selected.includes(seat.seatId);
                                return (
                                  <Seat
                                    key={seat.seatId}
                                    $status={status}
                                    $selected={isSel}
                                    title={`${reservedUnitLabel} ${seat.label}${
                                      classPrice[seat.seatClass]
                                        ? " · " + fmtMoney(classPrice[seat.seatClass])
                                        : ""
                                    }`}
                                    onClick={() => toggleSeat(seat)}
                                  >
                                    {seat.label}
                                  </Seat>
                                );
                              })}
                          </Seats>
                        </Row>
                      ))}
                    </Section>
                  ))}
                </MapWrap>
                <Legend>
                  <LegItem><Swatch $status="AVAILABLE" /> Available</LegItem>
                  <LegItem><Swatch $status="SELECTED" $selected /> Selected</LegItem>
                  <LegItem><Swatch $status="HELD" /> On hold</LegItem>
                  <LegItem><Swatch $status="SOLD" /> Sold</LegItem>
                </Legend>
              </>
            ) : (
              <GaWrap>
                {(showing.ticketTypes || []).map((tt) => {
                  const remaining =
                    tt.gaCapacity == null ? null : Math.max(0, tt.gaCapacity - (tt.gaSold || 0));
                  const q = gaQty[tt.key] || 0;
                  const soldOut = remaining === 0;
                  return (
                    <GaRow key={tt.key}>
                      <div>
                        <GaName>{tt.name}</GaName>
                        <GaPrice>
                          {fmtMoney(tt.price)}
                          {Number(tt.admissionsPerUnit || 1) > 1 ? ` · admits ${tt.admissionsPerUnit}` : ""}
                          {remaining != null ? ` · ${remaining} left` : ""}
                        </GaPrice>
                      </div>
                      <Stepper>
                        <StepBtn
                          disabled={q === 0}
                          onClick={() => setGaQty({ ...gaQty, [tt.key]: Math.max(0, q - 1) })}
                        >
                          −
                        </StepBtn>
                        <Qty>{q}</Qty>
                        <StepBtn
                          disabled={soldOut || (remaining != null && q >= remaining) || count >= MAX_UNITS}
                          onClick={() => setGaQty({ ...gaQty, [tt.key]: q + 1 })}
                        >
                          +
                        </StepBtn>
                      </Stepper>
                    </GaRow>
                  );
                })}
              </GaWrap>
            )}

            {notice && <Notice>{notice}</Notice>}

            <Bar>
              <BarInfo>
                <BarCount>{count} {count === 1 ? "unit" : "units"}{isReserved ? ` · ${reservedUnitLabel}` : ""}</BarCount>
                <BarTotal>{fmtMoney(total)}</BarTotal>
              </BarInfo>
              <Cta disabled={count === 0 || submitting} onClick={reserve}>
                {submitting ? "Reserving…" : currentUser ? "Reserve & continue" : "Sign in to book"}
              </Cta>
            </Bar>
          </>
        )}
      </Page>
    </>
  );
};

const Page = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: calc(var(--lusuku-nav-h, 130px) + 12px) 16px 120px;
  min-height: 60vh;
`;
const Muted = styled.div`color:#6b7280;padding:28px 0;`;
const Head = styled.div`
  margin-bottom: 18px;
  h1 { font-size: 22px; font-weight: 800; color:#1f2937; margin:0 0 4px; }
`;
const Meta = styled.div`color:#6b7280;font-size:13.5px;`;
const ShowMeta = styled.div`color:#374151;font-size:12.5px;font-weight:700;margin-top:5px;`;
const LiveInventory = styled.div`display:inline-flex;margin-top:9px;background:#ecfdf3;color:#067647;border:1px solid #abefc6;border-radius:20px;padding:4px 9px;font-size:10.5px;font-weight:800;`;
const Screen = styled.div`
  margin: 10px auto 26px;
  max-width: 520px;
  text-align: center;
  font-size: 11px;
  letter-spacing: 6px;
  color: #9ca3af;
  padding: 8px;
  border-top: 3px solid #cbd5e1;
  border-radius: 40% 40% 0 0 / 18px 18px 0 0;
  background: linear-gradient(180deg, #f1f5f9, transparent);
`;
const MapWrap = styled.div`overflow-x:auto;padding-bottom:8px;`;
const Section = styled.div`margin-bottom:14px;`;
const SecName = styled.div`font-size:12px;color:#6b7280;font-weight:700;margin:10px 0 6px;`;
const Row = styled.div`display:flex;align-items:center;gap:8px;margin:4px 0;justify-content:center;`;
const RowLabel = styled.div`width:16px;font-size:11px;color:#9ca3af;text-align:center;flex:0 0 16px;`;
const Seats = styled.div`display:flex;gap:6px;`;
const seatBg = (p) => {
  if (p.$selected) return "var(--cinema-primary)";
  switch (p.$status) {
    case "SOLD": return "#cbd5e1";
    case "HELD": return "#e5e7eb";
    case "BLOCKED": return "transparent";
    default: return "#ffffff";
  }
};
const Seat = styled.button`
  width: 26px;
  height: 26px;
  font-size: 9px;
  border-radius: 6px 6px 3px 3px;
  border: 1.4px solid ${(p) => (p.$selected ? "var(--cinema-primary)" : p.$status === "AVAILABLE" ? "var(--cinema-primary)" : "#d1d5db")};
  background: ${seatBg};
  color: ${(p) => (p.$selected ? "var(--cinema-on-primary)" : p.$status === "SOLD" || p.$status === "HELD" ? "#94a3b8" : "#475569")};
  cursor: ${(p) => (p.$status === "AVAILABLE" ? "pointer" : "not-allowed")};
  visibility: ${(p) => (p.$status === "BLOCKED" ? "hidden" : "visible")};
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.1s ease;
  &:hover { transform: ${(p) => (p.$status === "AVAILABLE" ? "scale(1.08)" : "none")}; }
`;
const Legend = styled.div`display:flex;gap:18px;flex-wrap:wrap;justify-content:center;margin-top:20px;`;
const LegItem = styled.div`display:flex;align-items:center;gap:6px;font-size:12px;color:#6b7280;`;
const Swatch = styled.span`
  width: 16px; height: 16px; border-radius: 4px; display: inline-block;
  border: 1.4px solid ${(p) => (p.$selected ? "var(--cinema-primary)" : p.$status === "AVAILABLE" ? "var(--cinema-primary)" : "#d1d5db")};
  background: ${seatBg};
`;
const GaWrap = styled.div`margin-top:8px;`;
const GaRow = styled.div`
  display:flex; align-items:center; justify-content:space-between;
  background:#fff; border:1px solid #e5e7eb; border-radius:12px; padding:14px 16px; margin-bottom:10px;
`;
const GaName = styled.div`font-weight:700;color:#1f2937;`;
const GaPrice = styled.div`font-size:12.5px;color:#6b7280;margin-top:3px;`;
const Stepper = styled.div`display:flex;align-items:center;gap:12px;`;
const StepBtn = styled.button`
  width:32px;height:32px;border-radius:8px;border:1px solid #d1d5db;background:#fff;font-size:18px;
  color:#1f2937;cursor:pointer;&:disabled{opacity:.4;cursor:not-allowed;}
`;
const Qty = styled.div`min-width:20px;text-align:center;font-weight:700;`;
const Notice = styled.div`margin-top:14px;color:#b45309;background:#fffbeb;border:1px solid #fde68a;padding:10px 12px;border-radius:8px;font-size:13px;`;
const Bar = styled.div`
  position: fixed; left: 0; right: 0; bottom: 0;
  background: #fff; border-top: 1px solid #e5e7eb; box-shadow: 0 -4px 16px rgba(0,0,0,.06);
  display: flex; align-items: center; justify-content: space-between;
  padding: 12px 20px; z-index: 40;
`;
const BarInfo = styled.div``;
const BarCount = styled.div`font-size:12px;color:#6b7280;`;
const BarTotal = styled.div`font-size:20px;font-weight:800;color:#1f2937;`;
const Cta = styled.button`
  background:var(--cinema-primary);color:var(--cinema-on-primary);border:none;border-radius:10px;padding:13px 22px;font-size:15px;font-weight:700;
  cursor:pointer;transition:filter .2s ease;&:hover{filter:brightness(.9);}
  &:disabled{opacity:.5;cursor:not-allowed;}
`;

export default SeatSelectionPage;
